import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { StatusListComponent } from '../../components/status-list/status-list.component';
import { StatusFormComponent } from '../../components/status-form/status-form.component';
import { StatusService } from '../../services/status.service';
import { CatalogService } from '@services/data/catalog.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import Swal from 'sweetalert2';
import { NgbModal, NgbModalRef, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-status-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StatusListComponent,
    StatusFormComponent,
    PageHeaderComponent,
    MobileFooterComponent,
    NgbModalModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './status-management.component.html',
  styleUrl: './status-management.component.scss'
})
export class StatusManagementComponent {
  @ViewChild(StatusListComponent) statusList!: StatusListComponent;
  @ViewChild('statusModal') statusModal: any;

  private modalRef?: NgbModalRef;
  selectedStatus: any = null;
  statusForm: FormGroup;
  mode = signal<FormMode>(FormMode.VIEW);
  formMode = FormMode;
  showDetail = false;

  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  private fb = inject(FormBuilder);
  private statusService = inject(StatusService);
  private catalogService = inject(CatalogService);
  private modalService = inject(NgbModal);

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  constructor() {
    this.statusForm = this.fb.group({
      id: [null],
      slug: [null, [Validators.required, Validators.pattern(/^[A-Z][A-Z0-9_]*$/)]],
      code: [null, [Validators.required]],
      name: [null, [Validators.required]],
      category: ['PROCESS', [Validators.required]],
      style: [null],
      icon: [null],
      approvedLabel: [null],
      isActive: [true]
    });
  }

  get showMobileDetail(): boolean {
    return this.appSettings.isMobile() && this.showDetail;
  }

  onStatusSelected(status: any) {
    this.openEditor(status, FormMode.EDIT);
  }

  private refreshAfterMutation() {
    this.statusList?.loadStatuses();
    this.catalogService.reloadStatuses();
  }

  private payloadFromForm() {
    const raw = this.statusForm.getRawValue();
    return {
      slug: String(raw.slug || '').trim().toUpperCase(),
      code: Number(raw.code),
      name: String(raw.name || '').trim(),
      category: String(raw.category || 'PROCESS').toUpperCase(),
      style: raw.style ? String(raw.style).trim() : null,
      icon: raw.icon ? String(raw.icon).trim() : null,
      approvedLabel: raw.approvedLabel ? String(raw.approvedLabel).trim() : null,
      isActive: !!raw.isActive
    };
  }

  saveStatus() {
    if (this.statusForm.invalid) {
      this.statusForm.markAllAsTouched();
      return;
    }

    const data = this.payloadFromForm();

    if (this.mode() === FormMode.EDIT) {
      if (!this.selectedStatus?.id) return;
      this.statusService.update(this.selectedStatus.id, data).subscribe({
        next: () => {
          this.Toast.fire({ icon: 'success', title: 'Estado actualizado correctamente' });
          this.refreshAfterMutation();
          this.mode.set(FormMode.VIEW);
          this.closeDetail();
        },
        error: (err) => {
          this.Toast.fire({
            icon: 'error',
            title: err?.error?.message || err?.error?.error || 'No se pudo actualizar el estado'
          });
        }
      });
      return;
    }

    if (this.mode() === FormMode.CREATE) {
      this.statusService.create(data).subscribe({
        next: () => {
          this.Toast.fire({ icon: 'success', title: 'Estado creado correctamente' });
          this.refreshAfterMutation();
          this.mode.set(FormMode.VIEW);
          this.closeDetail();
        },
        error: (err) => {
          this.Toast.fire({
            icon: 'error',
            title: err?.error?.message || err?.error?.error || 'No se pudo crear el estado'
          });
        }
      });
    }
  }

  async deactivateSelected() {
    if (!this.selectedStatus?.id) return;

    const result = await Swal.fire({
      title: '¿Desactivar estado?',
      text: `“${this.selectedStatus.name}” dejará de ofrecerse en selectores (Flujos BPM, etc.).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    this.statusService.update(this.selectedStatus.id, { ...this.payloadFromForm(), isActive: false }).subscribe({
      next: () => {
        this.Toast.fire({ icon: 'success', title: 'Estado desactivado' });
        this.refreshAfterMutation();
        this.closeDetail();
      },
      error: (err) => {
        this.Toast.fire({
          icon: 'error',
          title: err?.error?.message || err?.error?.error || 'No se pudo desactivar'
        });
      }
    });
  }

  open(content: any, size: 'md' | 'lg' | 'xl' = 'lg') {
    this.modalRef = this.modalService.open(content, { size });
  }

  close() {
    this.modalRef?.close();
  }

  closeDetail() {
    this.showDetail = false;
    this.selectedStatus = null;
    this.mode.set(FormMode.VIEW);
    this.close();
  }

  newStatusModal() {
    this.openEditor(null, FormMode.CREATE);
  }

  private openEditor(status: any | null, mode: FormMode) {
    this.selectedStatus = status;
    this.mode.set(mode);
    if (mode === FormMode.CREATE) {
      this.statusForm.reset({
        id: null,
        slug: null,
        code: null,
        name: null,
        category: 'PROCESS',
        style: 'border-secondary text-secondary',
        icon: 'fas fa-circle',
        approvedLabel: null,
        isActive: true
      });
      this.statusForm.get('slug')?.enable();
    } else {
      this.statusForm.reset();
      this.statusForm.patchValue({
        id: status.id,
        slug: status.slug,
        code: status.code,
        name: status.name,
        category: status.category || 'PROCESS',
        style: status.style,
        icon: status.icon || null,
        approvedLabel: status.approvedLabel || null,
        isActive: status.isActive !== false
      });
      this.statusForm.get('slug')?.disable();
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (this.appSettings.isMobile()) {
      this.showDetail = true;
      return;
    }
    this.open(this.statusModal);
  }
}
